import React from 'react';
import { format } from 'date-fns';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';

const FormSubmissionDetails = ({ isOpen, onClose, submission }) => {
  if (!submission) return null;

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'PPpp');
    } catch (err) {
      return 'Invalid date';
    }
  };

  return (
    <Modal size="3xl" isOpen={isOpen} onClose={onClose} title="Submission Details">
      <div className="bg-white rounded-lg p-6 max-w-7xl w-full max-h-[90vh] overflow-y-auto">

        {/* Form Information */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Form Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Form Name</p>
              <p className="font-medium">{submission.formName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Submission Date</p>
              <p className="font-medium">{formatDate(submission.submissionData?.submittedAt || submission.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="font-medium capitalize">{submission.status}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Submitted By</p>
              <p className="font-medium">{submission.userName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Deadline</p>
              <p className="font-medium">{formatDate(submission.deadlineDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Progress</p>
              <p className="font-medium">
                Documents: {submission.documentProgress.uploaded}/{submission.documentProgress.total},
                Verifications: {submission.verificationProgress.completed}/{submission.verificationProgress.total}
              </p>
            </div>
          </div>
        </div>

        {/* Client Information */}
        {submission.masterDataId && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Client Information</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Client Name</p>
                  <p className="font-medium">{submission.masterDataId.clientName}</p>
                </div>
                {submission.masterDataId.clientAddress && (
                  <div>
                    <p className="text-sm text-gray-500">Client Address</p>
                    <p className="font-medium">{submission.masterDataId.clientAddress}</p>
                  </div>
                )}
                {submission.masterDataId.contactPersons && submission.masterDataId.contactPersons.length > 0 && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500 mb-2">Contact Persons</p>
                    <div className="space-y-2">
                      {submission.masterDataId.contactPersons.map((contact, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium">{contact.title} {contact.name}</span>
                          <span className="text-gray-600"> - {contact.email}</span>
                          {contact.phoneNumbers && contact.phoneNumbers.length > 0 && (
                            <span className="text-gray-600"> ({contact.phoneNumbers.join(', ')})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {submission.masterDataId.authorizedSignatories && submission.masterDataId.authorizedSignatories.length > 0 && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500 mb-2">Authorized Signatories</p>
                    <div className="space-y-2">
                      {submission.masterDataId.authorizedSignatories.map((signatory, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium">{signatory.title} {signatory.designationName}</span>
                          {signatory.fatherName && (
                            <span className="text-gray-600"> - {signatory.fatherName}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form Fields */}
        {submission.submissionData?.formFields?.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Form Fields</h3>
            <div className="space-y-4">
              {submission.submissionData.formFields.map((field, index) => (
                <div key={index} className="border-b pb-2">
                  <p className="text-sm text-gray-500">{field.field?.label || field.name}</p>
                  <p className="font-medium">{field.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">Products</h3>
          {submission.submissionData?.products?.map((product, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-4">{product.name}</h4>
              <p className="text-sm text-gray-600 mb-4">{product.description}</p>

              {/* Required Fields */}
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-500 mb-2">Required Fields</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.requiredFields.map((field, fieldIndex) => (
                    <div key={fieldIndex}>
                      <p className="text-sm text-gray-500">{field.label}</p>
                      {field.fieldType === 'file' ? (
                        <a href={field.value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View File
                        </a>
                      ) : (
                        <p className="font-medium">{field.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Fees */}
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-500 mb-2">Fees</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{product.fees.governmentFees.name}</p>
                    <p className="font-medium">${product.fees.governmentFees.amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{product.fees.professionalFees.name}</p>
                    <p className="font-medium">${product.fees.professionalFees.amount.toFixed(2)}</p>
                  </div>
                  {product.fees.otherFees.map((fee, feeIndex) => (
                    <div key={feeIndex}>
                      <p className="text-sm text-gray-500">{fee.name}</p>
                      <p className="font-medium">${fee.amount.toFixed(2)}</p>
                    </div>
                  ))}
                </div> 
                <div className="mt-4 pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium text-gray-500">Total</p>
                    <p className="font-bold text-indigo-600">${product.totalFees?.toFixed(2) || 0}</p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-500 mb-2">Documents</h5>
                <div className="space-y-2">
                  {product.documents?.map((doc, docIndex) => (
                    <div key={docIndex}>
                      <p className="text-sm text-gray-500">{doc.requirementName}</p>
                      <a 
                        href={doc.document.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {doc.document.originalName}
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              {/* Verifications */}
              <div>
                <h5 className="text-sm font-medium text-gray-500 mb-2">Verifications</h5>
                <div className="space-y-2">
                  {product.verifications?.map((verification, verIndex) => (
                    <div key={verIndex} className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500">{verification.verificationType?.label}</p>
                        <p className="font-medium">{verification.value}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-sm capitalize ${
                        verification.status === 'approved' ? 'bg-green-100 text-green-800' :
                        verification.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {verification.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total Fees */}
        <div className="mt-6 pt-4 border-t">
          <div className="flex justify-between items-center text-xl font-semibold">
            <span>Total Fees</span>
            <span>${submission.submissionData?.totalFees?.toFixed(2) || 0}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FormSubmissionDetails; 